import React, { useState } from 'react';
import { Link } from 'react-router-dom';

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function formatPercent(value, { showSign = true, digits = 2 } = {}) {
  if (!isFiniteNumber(value)) return '—';
  return `${showSign && value >= 0 ? '+' : ''}${value.toFixed(digits)}%`;
}

function formatCount(value) {
  return Number.isInteger(value) ? String(value) : '—';
}

function getModeSummary(result, mode) {
  return Array.isArray(result?.mode_summaries)
    ? result.mode_summaries.find((summary) => summary.mode === mode)
    : null;
}

function getSelectedFilterDisplay(result) {
  return [
    result?.country || 'All Countries', result?.universe_label || 'Selected universe',
    result?.sector || 'All Sectors', result?.industry || 'All Industries',
  ].join(' · ');
}

function getWalkForwardCurrentTopN(comparison) {
  const rows = comparison?.matched_years;
  const current = Array.isArray(rows) && rows.length > 0
    ? Number(rows[rows.length - 1]?.walk_forward_selected_top_n) : null;
  return Number.isInteger(current) && current > 0 ? current : null;
}

function StrategyPlanExport({ exporting, exportError, onExport, disabled }) {
  return (
    <div className="validation-lab-synthesis-export-actions">
      <button type="button" onClick={onExport} disabled={exporting || disabled} className="validation-lab-btn-export">
        {exporting ? 'Exporting Full Statistical Report PDF...' : 'Export Full Statistical Report PDF'}
      </button>
      {exportError && <div className="validation-lab-error" role="alert"><span>{exportError} The displayed Strategy Plan result is retained.</span></div>}
    </div>
  );
}

function WhatToDo({ steps }) {
  return (
    <section className="validation-lab-synthesis-plan-card validation-lab-synthesis-what-to-do-card">
      <h4>What To Do</h4>
      <div className="validation-lab-table-wrapper"><table className="validation-lab-table validation-lab-synthesis-table validation-lab-synthesis-what-to-do-table">
        <colgroup><col /><col /></colgroup>
        <thead><tr><th>Step</th><th>What To Do</th></tr></thead>
        <tbody>{steps.map((step, index) => <tr key={step}><td>{index + 1}</td><td>{step}</td></tr>)}</tbody>
      </table></div>
      
    </section>
  );
}

function ComparisonTables({ summary, selectedPortfolioObjectiveOption }) {
  const [yearsOpen, setYearsOpen] = useState(false);
  const comparison = summary?.fixed_vs_walk_forward_comparison;
  const fixedTopN = comparison?.fixed_top_n || summary?.selected_full_sample_top_n;
  const matchedCount = comparison?.matched_period_count;
  const winner = comparison?.winner;
  const fixedWinner = winner === 'fixed_top_n';
  const walkWinner = winner === 'walk_forward';
  const currentWalkTopN = getWalkForwardCurrentTopN(comparison);

  if (!comparison?.comparison_available) {
    const missingDates = comparison?.missing_fixed_test_snapshot_dates || [];
    return (
      <section className="validation-lab-synthesis-plan-card" aria-label="Strategy comparison">
        <h4>Strategy Comparison</h4>
        <p>A complete Fixed Top N comparison is not available for every Walk-Forward test year.{missingDates.length > 0 ? ` Missing dates: ${missingDates.join(', ')}.` : ''}</p>
      </section>
    );
  }

  const comparisonRows = fixedWinner
    ? [
        [`Fixed Top ${fixedTopN} advantage`, formatPercent(comparison.fixed_vs_walk_compounded_difference_pp)],
        ['Annualized advantage', `${formatPercent(comparison.fixed_vs_walk_annualized_difference_pp)} per year`],
        [`Fixed Top ${fixedTopN} beat Walk-Forward`, `${formatCount(comparison.fixed_beats_walk_period_count)} of ${formatCount(matchedCount)} years`],
      ]
    : walkWinner
      ? [
          ['Walk-Forward advantage', formatPercent(-comparison.fixed_vs_walk_compounded_difference_pp)],
          ['Annualized advantage', `${formatPercent(-comparison.fixed_vs_walk_annualized_difference_pp)} per year`],
          ['Walk-Forward beat Fixed Top N', `${formatCount(matchedCount - comparison.fixed_beats_walk_period_count)} of ${formatCount(matchedCount)} years`],
        ]
      : [['Comparison', 'Similar Results']];
  const recommendedStrategy = fixedWinner ? 'Fixed Top N' : walkWinner ? 'Walk-Forward Top N' : 'Similar Results';
  const recommendedTarget = fixedWinner ? `Top ${fixedTopN}` : walkWinner ? (currentWalkTopN ? `Top ${currentWalkTopN} currently` : 'Changes each year') : 'Review both results';
  const recommendedHowItWorks = fixedWinner
    ? `Keep the portfolio size at ${fixedTopN} stocks. Use the newest ranking when updating the portfolio.`
    : walkWinner
      ? 'Before each yearly update, recalculate the Top N from the historical evidence available at that time.'
      : 'Both methods had the same unrounded annualized return over the matched years.';
  const whyThisPlan = fixedWinner
    ? `Why this plan: Fixed Top ${fixedTopN} produced the better result when both strategies were tested over the same years.`
    : walkWinner
      ? 'Why this plan: Walk-Forward produced the better result when both strategies were tested over the same years.'
      : 'Why this plan: both methods had the same unrounded annualized result when tested over the same years.';
  const walkTarget = currentWalkTopN ? `Top ${currentWalkTopN}` : 'the current Walk-Forward Top N';
  const allocationMethod = selectedPortfolioObjectiveOption.mode === 'balanced'
    ? 'Balanced = Equal Weight' : selectedPortfolioObjectiveOption.secondaryText;
  const steps = fixedWinner
    ? [
        'Run the current selected-universe scan using the same Country, Sector and Industry filters.',
        `Take the first ${fixedTopN} stocks from the current ranking.`,
        `Add them to a portfolio and use Portfolio Balancing to divide the money according to the selected method, with ${allocationMethod}.`,
        `Keep Alerts active and review portfolio changes regularly. Updating the portfolio monthly when stocks fall outside the Top ${fixedTopN} could potentially improve results further.`,
        `At minimum, once per year run the latest ranking again and replace stocks no longer inside Top ${fixedTopN} with newly qualifying stocks.`,
        'Run Portfolio Balancing again after replacements to restore the intended allocations.',
      ]
    : [
        'Run the current selected-universe scan using the same Country, Sector and Industry filters.',
        `Use the current Walk-Forward Top N supplied by the plan (${walkTarget}) from the current ranking.`,
        `Add those stocks to a portfolio and use Portfolio Balancing to divide the money according to the selected method, with ${allocationMethod}.`,
        'Keep Alerts active and review portfolio changes regularly.',
        'At minimum, once per year update the Walk-Forward Top N using the historical evidence then available, and replace stocks that no longer qualify.',
        'Run Portfolio Balancing again after replacements to restore the intended allocations.',
      ];

  return (
    <>
      <section className="validation-lab-synthesis-plan-card" aria-label="Strategy comparison">
      <h4>Strategy Comparison</h4>
      <div className="validation-lab-table-wrapper"><table className="validation-lab-table validation-lab-synthesis-table validation-lab-synthesis-strategy-table">
        <colgroup><col /><col /><col /><col /><col /><col /></colgroup>
        <thead><tr><th>Strategy</th><th>Top N</th><th>Years Tested</th><th>Compounded Return</th><th>Annualized Return</th><th>Beat S&amp;P 500</th></tr></thead>
        <tbody>
          <tr><td>Fixed Top N</td><td>Top {fixedTopN} every year</td><td>{formatCount(matchedCount)}</td><td>{formatPercent(comparison.fixed_compounded_return_percent)}</td><td>{formatPercent(comparison.fixed_annualized_return_percent)}</td><td>{formatCount(comparison.fixed_spy_win_count)} of {formatCount(matchedCount)}</td></tr>
          <tr><td>Walk-Forward</td><td>Changes each year</td><td>{formatCount(matchedCount)}</td><td>{formatPercent(comparison.walk_forward_compounded_return_percent)}</td><td>{formatPercent(comparison.walk_forward_annualized_return_percent)}</td><td>{formatCount(comparison.walk_forward_spy_win_count)} of {formatCount(matchedCount)}</td></tr>
          <tr><td>S&amp;P 500</td><td>—</td><td>{formatCount(matchedCount)}</td><td>{formatPercent(comparison.spy_compounded_return_percent)}</td><td>{formatPercent(comparison.spy_annualized_return_percent)}</td><td>—</td></tr>
        </tbody>
      </table></div>
      <div className="validation-lab-table-wrapper"><table className="validation-lab-table validation-lab-synthesis-table validation-lab-synthesis-comparison-summary-table">
        <colgroup><col /><col /></colgroup>
        <thead><tr><th>Comparison</th><th>Result</th></tr></thead>
        <tbody>{comparisonRows.map(([label, value]) => <tr key={label}><td>{label}</td><td>{value}</td></tr>)}</tbody>
      </table></div>

      <h4>Recommended Plan</h4>
      <div className="validation-lab-table-wrapper"><table className="validation-lab-table validation-lab-synthesis-table validation-lab-synthesis-recommended-plan-table">
        <colgroup><col /><col /><col /></colgroup>
        <thead><tr><th>Best Strategy</th><th>Target</th><th>How It Works</th></tr></thead>
        <tbody><tr><td>{recommendedStrategy}</td><td>{recommendedTarget}</td><td>{recommendedHowItWorks}</td></tr></tbody>
      </table></div>
      <p>{whyThisPlan}</p>

      <button type="button" className="validation-lab-btn-secondary" aria-expanded={yearsOpen} onClick={() => setYearsOpen((open) => !open)}>{yearsOpen ? 'Hide Each Year' : 'See Each Year'}</button>
      {yearsOpen && <div className="validation-lab-table-wrapper"><table className="validation-lab-table validation-lab-synthesis-table validation-lab-synthesis-years-table">
        <colgroup><col /><col /><col /><col /><col /><col /></colgroup>
        <thead><tr><th>Starting Year</th><th>Fixed Top N Return</th><th>Walk-Forward Top N</th><th>Walk-Forward Return</th><th>S&amp;P 500 Return</th><th>Better Strategy</th></tr></thead>
        <tbody>{comparison.matched_years.map((row) => <tr key={row.date}><td>{row.date}</td><td>{formatPercent(row.fixed_top_n_return_percent)}</td><td>Top {row.walk_forward_selected_top_n}</td><td>{formatPercent(row.walk_forward_return_percent)}</td><td>{formatPercent(row.spy_return_percent)}</td><td>{row.better_strategy === 'fixed_top_n' ? 'Fixed Top N' : row.better_strategy === 'walk_forward' ? 'Walk-Forward' : 'Tie'}</td></tr>)}</tbody>
      </table></div>}
      </section>
      <WhatToDo steps={steps} />
    </>
  );
}

function StrategySynthesisResults({ result, selectedPortfolioObjectiveOption, exporting, exportError, onExport, exportBlocked }) {
  return <div className="validation-lab-synthesis-results">
    <div className="validation-lab-synthesis-context"><h4 className="validation-lab-synthesis-context-title">{result.universe_label} Strategy Plan Evidence</h4><p>Selected Filter: {getSelectedFilterDisplay(result)}</p></div>
    <ComparisonTables summary={getModeSummary(result, selectedPortfolioObjectiveOption.mode)} selectedPortfolioObjectiveOption={selectedPortfolioObjectiveOption} />
    <StrategyPlanExport exporting={exporting} exportError={exportError} onExport={onExport} disabled={exportBlocked} />
  </div>;
}

function StrategySynthesisSection({ selectedUniverseOption, portfolioObjectives, selectedPortfolioObjective, selectedPortfolioObjectiveOption, result, loading, error, exporting, exportError, requestBlocked, exportBlocked, onRun, onExport, onPortfolioObjectiveChange, onClear }) {
  const showClear = Boolean(result || error);
  return <div className="validation-lab-section validation-lab-synthesis-section">
    <div className="validation-lab-section-header"><div>
      <span className="validation-lab-analysis-badge validation-lab-analysis-badge-walkforward">WALK-FORWARD TEST</span>
      <h3 className="validation-lab-section-title">4. Compare Strategies and Build a Plan</h3>
      <p className="validation-lab-section-description">Step 3 found the Best Historical Top N using the full historical period. Here we compare keeping that Top N fixed with a Walk-Forward strategy.</p>
      <p className="validation-lab-section-description">Unlike the historical tests above, Walk-Forward does not use the full history to choose the best settings. It uses only the data available at that time to choose a Top N, then tests that choice on the next year and compares the result with the S&amp;P 500 benchmark. It repeats this process year by year.</p>
      <p className="validation-lab-report-definition">Example: using data available up to 2020, the model might choose Top 20. It then tests that Top 20 on 2021. After that, it adds the 2021 data, chooses the settings again, and tests them on 2022.</p>
      <p className="validation-lab-section-description">The table below compares the Fixed Top N and Walk-Forward Top N over the same years, so their results can be compared directly.</p>
      <Link to="/methodology#strategy-lab-walk-forward" className="validation-lab-methodology-link">See how walk-forward testing works →</Link>
    </div><button type="button" onClick={onRun} disabled={loading || requestBlocked} className="validation-lab-btn-primary">{loading ? `Building ${selectedUniverseOption.label} strategy plan...` : 'Build Strategy Plan'}</button></div>
    <fieldset className="validation-lab-synthesis-objectives"><legend>Portfolio method</legend><div className="validation-lab-synthesis-objective-grid">
      {portfolioObjectives.map((objective) => <label key={objective.value} className={`validation-lab-synthesis-objective-card ${selectedPortfolioObjective === objective.value ? 'selected' : ''}`}><input type="radio" name="validation-lab-portfolio-objective" value={objective.value} checked={selectedPortfolioObjective === objective.value} onChange={() => onPortfolioObjectiveChange(objective.value)} /><span className="validation-lab-synthesis-objective-label">{objective.label}</span><span className="validation-lab-synthesis-objective-copy">{objective.beginnerText}</span><span className="validation-lab-synthesis-objective-method">{objective.secondaryText}</span></label>)}
    </div></fieldset>
    <div className="validation-lab-synthesis-actions">{showClear && <button type="button" onClick={onClear} disabled={loading} className="validation-lab-btn-secondary">Clear Strategy Plan</button>}</div>
    {loading && <div className="validation-lab-status-message validation-lab-status-warning" role="status"><p>Building a Strategy Plan for {selectedUniverseOption.label}.</p></div>}
    {error && <div className="validation-lab-error" role="alert"><span>{error} The last successful Strategy Plan result is retained until you rerun or clear it.</span></div>}
    {!loading && !result && <div className="validation-lab-empty-card"><p className="validation-lab-empty-message">Build a Strategy Plan for {selectedUniverseOption.label}.</p></div>}
    {result && <StrategySynthesisResults result={result} selectedPortfolioObjectiveOption={selectedPortfolioObjectiveOption} exporting={exporting} exportError={exportError} exportBlocked={exportBlocked} onExport={onExport} />}
  </div>;
}

export default StrategySynthesisSection;
